import React from "react";
import { CaretUp, CaretDown } from "@phosphor-icons/react";
import { useAnalytics } from "@/components/assets/useAssets";

// Live ADA market price from /api/network/price (CoinGecko-backed, server-cached).
// Shared across the global header (chip) and the native-assets dashboard (card).
export const useAdaPrice = () =>
  useAnalytics("/network/price", {}, { ttl: 60000, refreshMs: 60000 });

const fmtPrice = (p) => {
  const n = Number(p);
  if (!Number.isFinite(n)) return "—";
  return `$${n < 1 ? n.toFixed(4) : n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtCap = (c) => {
  const n = Number(c);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `$${new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(n)}`;
};

const changeTone = (pct) => {
  const n = Number(pct);
  if (!Number.isFinite(n) || n === 0) return { cls: "text-muted-foreground", Icon: null, sign: "" };
  return n > 0
    ? { cls: "text-emerald-400", Icon: CaretUp, sign: "+" }
    : { cls: "text-red-400", Icon: CaretDown, sign: "" };
};

// Compact header chip: ₳ $0.19  ▴ +1.2%
export const AdaPriceChip = () => {
  const { data, loading } = useAdaPrice();
  const price = data?.price;
  const pct = data?.change_24h_pct;
  const { cls, Icon, sign } = changeTone(pct);
  const title = data
    ? `ADA / USD · market cap ${fmtCap(data.market_cap)} · 24h ${sign}${Number(pct).toFixed(2)}%${data.stale ? " · delayed" : ""} · source: ${data.source || "market"}`
    : "ADA price";

  return (
    <div
      data-testid="ada-price-chip"
      title={title}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-primary/25 bg-primary/5 normal-case tracking-normal"
    >
      <span className="font-heading text-[12px] text-primary leading-none">₳</span>
      {loading && !data ? (
        <span data-testid="ada-price-chip-value" className="mono text-[12px] text-muted-foreground/60">—</span>
      ) : (
        <>
          <span data-testid="ada-price-chip-value" className="mono text-[12px] text-foreground leading-none">{fmtPrice(price)}</span>
          {Number.isFinite(Number(pct)) ? (
            <span data-testid="ada-price-chip-change" className={`inline-flex items-center gap-0.5 mono text-[10px] leading-none ${cls}`}>
              {Icon ? <Icon size={9} weight="bold" /> : null}
              {sign}{Number(pct).toFixed(1)}%
            </span>
          ) : null}
        </>
      )}
    </div>
  );
};

// Dashboard market card: richer, matches the ChainTip tile styling.
export const AdaMarketCard = () => {
  const { data, loading } = useAdaPrice();
  const price = data?.price;
  const pct = data?.change_24h_pct;
  const { cls, Icon, sign } = changeTone(pct);

  return (
    <div data-testid="dashboard-ada-price" className="rounded-xl border border-primary/20 bg-card/40 backdrop-blur-md px-4 py-2.5 min-w-[180px]">
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-muted-foreground mono">
        ada / usd {data?.stale ? <span className="text-amber-400/80">· delayed</span> : null}
      </div>
      <div className="flex items-baseline gap-2">
        <div className="font-heading text-[16px] leading-tight text-[#22d3ee]">
          {loading && !data ? "—" : fmtPrice(price)}
        </div>
        {Number.isFinite(Number(pct)) ? (
          <span data-testid="dashboard-ada-change" className={`inline-flex items-center gap-0.5 mono text-[11px] ${cls}`}>
            {Icon ? <Icon size={10} weight="bold" /> : null}
            {sign}{Number(pct).toFixed(2)}%
          </span>
        ) : null}
      </div>
      <div className="text-[10px] mono text-muted-foreground/70">
        Mkt cap {loading && !data ? "—" : fmtCap(data?.market_cap)}
      </div>
    </div>
  );
};
